import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { hasPermission, type Permission, type Role } from '@iecn/shared';
import { api, clearTokens, getAccessToken, setTokens } from './api';

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  roles: Role[];
  permissions: Permission[];
  home: string;
  driver: { id: string; status: string; approvalState: string; locationSharing: boolean } | null;
  organizations: Array<{ id: string; name: string }>;
}

interface AuthValue {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<CurrentUser>;
  signOut: () => Promise<void>;
  can: (permission: Permission) => boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api<CurrentUser>('/auth/me'));
    } catch {
      clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api<{
      accessToken: string;
      refreshToken: string;
      user: CurrentUser;
    }>('/auth/login', { method: 'POST', body: { email, password }, auth: false });
    setTokens(result.accessToken, result.refreshToken);
    setUser(result.user);
    return result.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // Signing out locally must succeed even if the server call does not.
    }
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      refresh: load,
      can: (permission) => (user ? hasPermission(user.roles, permission) : false),
    }),
    [user, loading, signIn, signOut, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
